import {
  faBook,
  faFileAlt,
  faFileLines,
  faGavel,
  faLandmark,
  faLightbulb,
  faUser,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getContentAfterHeadings } from "../../utils/HelperFunction/textFormatting";
import AskChatbot from "../layout/AskChatbot";
import { getOriginalJudgement } from "../../utils/HelperFunction/getOriginalJudgement";

const CaseDetailJudgementTab = ({
  searchTerm,
  currentMatch,
  setCurrentMatch,
  setTotalMatches,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const caseDetail = useSelector((state) => state.caseDetail);
  const { caseItem } = caseDetail;
  const previousMatchRef = useRef(null);
  const contentRef = useRef(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [highlightedHtml, setHighlightedHtml] = useState("");
  const [openAskChatbot, setOpenAskChatbot] = useState(false);

  const toggleAskChatbot = () => {
    setOpenAskChatbot((prev) => !prev);
  };

  const getAppellant = (title) => {
    return title ? title.split("Versus")[0] : "";
  };

  const getRespondents = (title) => {
    return title ? title.split("Versus")[1] : "";
  };

  const caseContentWithMetadata = `
  Title: ${caseItem?.Title || ""}
  Citation: ${caseItem?.Citation_Name || ""}
  Court: ${caseItem?.Court || ""}
  Judge: ${caseItem?.judge_name_clean || ""}
  Appellant: ${getAppellant(caseItem?.Title)}
  Respondents: ${getRespondents(caseItem?.Title)}
  ${getOriginalJudgement(caseItem?.Case_Description)}
`;

  const judgementSections = [
    {
      label: "Facts",
      color: "#FEF3C7",
      textColor: "#D97706",
      icon: faFileAlt,
      section: "facts",
      available: caseItem?.facts?.length > 0,
    },
    {
      label: "Issues",
      color: "#FEE2E2",
      textColor: "#DC2626",
      icon: faLightbulb,
      section: "issues",
      available: caseItem?.issues?.length > 0,
    },
    {
      label: "Petitioner's Arguments",
      color: "#EFF6FF",
      textColor: "#2563EB",
      icon: faUser,
      section: "argument_by_petitioner",
      available: caseItem?.argument_by_petitioner?.length > 0,
    },
    {
      label: "Respondent's Arguments",
      color: "#F5F3FF",
      textColor: "#7C3AED",
      icon: faUserTie,
      section: "argument_by_responses",
      available: caseItem?.argument_by_responses?.length > 0,
    },
    {
      label: "Analysis of the Law",
      color: "#ECFDF5",
      textColor: "#059669",
      icon: faGavel,
      section: "section_analysis",
      available: caseItem?.section_analysis?.length > 0,
    },
    {
      label: "Precedent Analysis",
      color: "#FDF2F8",
      textColor: "#DB2777",
      icon: faBook,
      section: "precedent_analysis",
      available: caseItem?.precedent_analysis?.length > 0,
    },
    {
      label: "Court's Reasoning",
      color: "#F0F9FF",
      textColor: "#0284C7",
      icon: faLandmark,
      section: "courts_reasoning",
      available: caseItem?.courts_reasoning?.length > 0,
    },
    {
      label: "Conclusion",
      color: "#FDF4FF",
      textColor: "#9333EA",
      icon: faFileLines,
      section: "conclusion",
      available: caseItem?.conclusion?.length > 0,
    },
  ];

  const handleSectionChange = (value) => {
    setSelectedSections((prev) => {
      // Check if the value is already in the array
      const isValueIncluded = prev?.includes(value);

      !isValueIncluded &&
        setTimeout(() => {
          const sectionElement = document.getElementById(`section-${value}`);
          if (sectionElement) {
            sectionElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      // If included, remove it; otherwise, add it
      const updatedItems = isValueIncluded
        ? prev?.filter((item) => item !== value) // Remove the value
        : [...prev, value]; // Add the value

      return updatedItems;
    });
  };

  const highlightText = (html, search) => {
    if (!search) return { html, matchCount: 0 };

    // Escape special characters in the search term
    const escapedTerm = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(
      `(${escapedTerm.replace(/\s+/g, "(\\s|<[^>]+>|\\n|\\r)*")})`,
      "gi",
    );
    let matchCount = 0;
    // Function to highlight text recursively
    const highlightNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const highlightedText = node.textContent.replace(regex, (match) => {
          matchCount++;
          return `<span class="highlight" data-index="${matchCount}">${match}</span>`;
        });
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = highlightedText;
        return tempDiv.innerHTML;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes)
          .map(highlightNode)
          .join("");
        const element = document.createElement(node.nodeName.toLowerCase());
        Array.from(node.attributes).forEach((attr) => {
          element.setAttribute(attr.name, attr.value);
        });
        element.innerHTML = children;
        return element.outerHTML;
      }

      return "";
    };

    // Parse the input HTML and highlight matches
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const highlightedHtml = Array.from(tempDiv.childNodes)
      .map(highlightNode)
      .join("");

    return { html: highlightedHtml, matchCount };
  };

  const highlightParatagsText = (text, searchTerm) => {
    if (!searchTerm) {
      return { html: text, matchCount: 0 };
    }

    const regex = new RegExp(`(${searchTerm})`, "gi");
    let matchCount = 0;

    const html = text.replace(regex, (match) => {
      matchCount++;
      return `<span style="background-color: yellow; font-weight: bold;">${match}</span>`;
    });

    return { html, matchCount };
  };

  useEffect(() => {
    let styledSections = "";
    let totalMatches = 0;

    if (selectedSections?.length > 0) {
      // Generate content with dynamic styles for checked items
      styledSections = selectedSections
        .map((key) => {
          const tagItem = judgementSections.find(
            (item) => item.section === key,
          );
          if (!tagItem || !caseItem[key]) return "";

          const sectionContent = caseItem[key]
            .map((item) => {
              const highlighted = highlightParatagsText(item, searchTerm);
              totalMatches += highlighted.matchCount; // Count matches for each section
              return `<div>${highlighted.html}</div>`;
            })
            .join("");

          return `
            <div style="margin-top: 12px;">
              <div style="background-color: ${tagItem.color}; font-size: 14px; font-weight: bold; padding-left: 5px;">
                ${tagItem.label}
              </div>
              <div>${sectionContent}</div>
            </div>
          `;
        })
        .join("");
    } else {
      // Generate content for the default case
      const result = highlightText(
        getContentAfterHeadings(caseItem?.Case_Description_HTML || ""),
        searchTerm,
      );
      styledSections = `
        <div class="default-content">
          ${result.html}
        </div>
      `;
      totalMatches = result.matchCount;
    }

    const styledHtml = `
      <style>
        .MsoNormal {
          font-family: 'Roboto', Helvetica, Arial, sans-serif !important;
          font-size: 13px;
          text-align: justify;
        }
        .MsoNormal b {
          color: #303f9f;
        }

        p {
        margin-bottom: 16px;
        color: #334155;
      }

      a {
      display: none
      }

      u {
      font-style: normal;
      }

      strong {
       font-style: normal;
      }

        img {
        display:none
      }

        .highlight-section {
          background-color: yellow;
          font-weight: bold;
        }

      @media (max-width: 480px) {
        .MsoNormal {
          font-size: 12px;
        }
        p {
          font-size: 13px;
        }

      </style>
      ${styledSections}
    `;
    // Set the generated HTML and match data
    setHighlightedHtml(styledHtml);
    setTotalMatches(totalMatches);
    setCurrentMatch(totalMatches > 0 ? 1 : 0);
  }, [searchTerm, caseItem, selectedSections]);

  useEffect(() => {
    const previous = document.querySelector(
      `.highlight[data-index="${previousMatchRef.current}"]`,
    );
    const next = document.querySelector(
      `.highlight[data-index="${currentMatch}"]`,
    );

    // Remove border from previous match
    if (previous) {
      previous.style.border = "none";
    }

    // Highlight current match
    if (next) {
      next.scrollIntoView({ behavior: "smooth", block: "center" });
      next.style.border = "1px solid #303f9f";
    }

    // Update previous match index
    previousMatchRef.current = currentMatch;
  }, [currentMatch]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Combined Quick Navigation and Judgment Content */}
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          width: { xs: "100%", lg: "calc(100% - 280px)" },
          height: "100%",
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          overflow: "auto",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          flexDirection: "column",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "4px",
          },
        }}
      >
        {/* Quick Navigation Header */}
        <Box
          sx={{
            p: 1.5,
            boxSizing: "border-box",
            borderBottom: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#EFF6FF",
                  borderRadius: "6px",
                  color: "#2563EB",
                  boxShadow: "0 1px 2px rgba(37, 99, 235, 0.1)",
                }}
              >
                <FontAwesomeIcon
                  icon={faLightbulb}
                  style={{ fontSize: "0.75rem" }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#1E293B",
                    mb: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  Quick Navigation
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748B",
                    fontSize: "0.7rem",
                    lineHeight: 1.2,
                  }}
                >
                  Click on any section to highlight it in the judgment
                </Typography>
              </Box>
            </Box>
            <Chip
              size="small"
              label="Reset Highlights"
              onClick={() => {
                try {
                  setSelectedSections([]);
                  // Remove any existing highlights
                  const highlightedElements =
                    document.querySelectorAll(".highlight-section");
                  highlightedElements.forEach((el) => {
                    el.classList.remove("highlight-section");
                  });
                  contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                } catch (error) {
                  console.error("Error resetting highlights:", error);
                }
              }}
              sx={{
                bgcolor: "#F8FAFC",
                color: "#475569",
                border: "1px solid #E2E8F0",
                fontSize: "0.7rem",
                height: "24px",
                "&:hover": {
                  bgcolor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
                "& .MuiChip-label": {
                  px: 1.25,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            {judgementSections.map(
              (
                { label, icon, color, textColor, section, available },
                index,
              ) => {
                const isSelected = selectedSections?.includes(section);

                const chip = (
                  <span style={{ display: "inline-block" }}>
                    <Chip
                      key={`${label}-${index}`}
                      label={label}
                      onClick={
                        available
                          ? () => handleSectionChange(section)
                          : undefined
                      }
                      icon={
                        <FontAwesomeIcon
                          icon={icon}
                          style={{
                            fontSize: "0.7rem",
                            color: available ? textColor : "#94A3B8",
                          }}
                        />
                      }
                      sx={{
                        pointerEvents: available ? "auto" : "none",
                        bgcolor: isSelected ? color : "#F8FAFC",
                        color: available
                          ? isSelected
                            ? textColor
                            : "#475569"
                          : "#94A3B8",
                        border: `1px solid ${
                          available
                            ? isSelected
                              ? textColor
                              : "#E2E8F0"
                            : "#CBD5E1"
                        }`,
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "0.7rem",
                        height: "28px",
                        transition: "all 0.2s ease",
                        "&:hover": available && {
                          bgcolor: color,
                          color: textColor,
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                        },
                        "& .MuiChip-label": {
                          px: 1.25,
                        },
                      }}
                    />
                  </span>
                );

                return available ? (
                  chip
                ) : (
                  <Tooltip
                    key={label}
                    title={`No ${label} available for this case`}
                    arrow
                    placement="top"
                  >
                    {chip}
                  </Tooltip>
                );
              },
            )}
          </Box>
        </Box>

        {/* Judgment Content */}
        <Box
          ref={contentRef}
          sx={{
            p: { xs: 2, md: 2.5 },
            boxSizing: "border-box",
            flex: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "4px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", lg: "flex-start" },
              gap: 1,
              mb: 2,
              px: 0.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#EFF6FF",
                  borderRadius: "6px",
                  color: "#2563EB",
                }}
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  style={{ fontSize: "0.75rem" }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "#1E293B",
                  letterSpacing: "-0.01em",
                }}
              >
                Original Judgment
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={toggleAskChatbot}
              sx={{
                display: { xs: "flex", lg: "none" },
                bgcolor: "#2563EB",
                color: "#FFFFFF",
                p: 0,
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: "#1D4ED8",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
                },
                transition: "all 0.2s ease",
                minWidth: "24px",
                height: "24px",
              }}
            >
              <FontAwesomeIcon
                icon={faLightbulb}
                style={{ fontSize: "0.75rem" }}
              />
            </IconButton>
          </Box>
          {selectedSections?.length === 0 ? (
            <Box
              sx={{
                wordBreak: "break-word",
                fontFamily: "'Roboto', Helvetica, Arial, sans-serif",
                fontSize: "13px",
                lineHeight: 1.3,
              }}
              dangerouslySetInnerHTML={{
                __html: highlightedHtml,
              }}
            />
          ) : (
            <Box>
              {selectedSections.map((key, index) => {
                const tagItem = judgementSections.find(
                  (item) => item.section === key,
                );
                if (!tagItem || !caseItem[key]) return null;

                return (
                  <div
                    key={`${key}-${index}`}
                    id={`section-${key}`}
                    style={{ marginTop: "12px" }}
                  >
                    <Typography
                      sx={{
                        backgroundColor: tagItem.color,
                        fontSize: "13px",
                        fontWeight: "bold",
                        paddingLeft: "5px",
                      }}
                    >
                      {tagItem.label}
                    </Typography>
                    <List>
                      {caseItem[key].map((item, index) => {
                        const highlighted = highlightText(item, searchTerm);

                        return (
                          <ListItem key={`${item}-${index}`}>
                            <ListItemText
                              primaryTypographyProps={{
                                fontSize: "13px",
                                backgroundColor: tagItem.color,
                                color: "#334155",
                              }}
                              primary={
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: highlighted.html,
                                  }}
                                />
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </div>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
      {isMobileView ? (
        <Drawer
          anchor="right"
          open={openAskChatbot}
          ModalProps={{
            keepMounted: true,
          }}
          onClose={toggleAskChatbot}
        >
          {" "}
          <AskChatbot
            chatContext={caseContentWithMetadata}
            component={"case"}
          />
        </Drawer>
      ) : (
        <AskChatbot chatContext={caseContentWithMetadata} component={"case"} />
      )}
    </Box>
  );
};

export default CaseDetailJudgementTab;
